import React from 'react';
import moment from 'moment';
import { ContentCode, ContentCodeStartType, ContentCodeActivation, ContentCodeEditData } from 'models/Content';
import { AppServices } from 'services/AppServices';
import Locale from 'localization/Localization';
import { Switch } from 'components/common/Switch';
import { Spinner } from 'components/common/Spinner';
import { ResultMessage, ResultStyle, ResultMessageView } from '../common/ResultMessageView';
import Clipboard from 'react-clipboard.js';
import { isValidEmailAddress } from 'util/Utils';
import { UserProvider, UserConsumer } from 'components/contexts/UserContext';

interface ContentCodeDetailsViewProps {
	contentCode?: ContentCode;
}

interface ContentCodeDetailsViewState {
	contentCode?: ContentCode;
	activations?: ContentCodeActivation[];

	loadInProgress: boolean;
	codeEdited: boolean;

	active: boolean;
	startType: ContentCodeStartType;
	startTime: moment.Moment;
	validFrom: moment.Moment;
	validThru: moment.Moment;
	maxActivations: number;

	resultMessage?: ResultMessage;
	clipboardMessage?: ResultMessage;
}

export class ContentCodeDetailsView extends React.Component<ContentCodeDetailsViewProps, ContentCodeDetailsViewState>
{
	constructor(props: ContentCodeDetailsViewProps) {
		super(props);

		if (this.props.contentCode === undefined) {
			this.state = {
				loadInProgress: true,
				codeEdited: false,
				active: false,
				startType: ContentCodeStartType.Activation,
				startTime: moment(),
				validFrom: moment(),
				validThru: moment(),
				maxActivations: 1
			};
		}
		else {
			this.state = {
				contentCode: this.props.contentCode,
				activations: undefined,
				loadInProgress: true,
				codeEdited: false,
				active: this.props.contentCode.active,
				startType: this.props.contentCode.startType,
				startTime: moment.utc(this.props.contentCode.startTime),
				validFrom: moment.utc(this.props.contentCode.validFrom),
				validThru: moment.utc(this.props.contentCode.validThru),
				maxActivations: this.props.contentCode.maxActivations
			};
		}


		this.getActivations = this.getActivations.bind(this);
		this.onEdit = this.onEdit.bind(this);
		this.setCodeState = this.setCodeState.bind(this);
		this.setActivationState = this.setActivationState.bind(this);

		this.onSubmitChanges = this.onSubmitChanges.bind(this);
		this.onEditValidThru = this.onEditValidThru.bind(this);
		this.onEditValidFrom = this.onEditValidFrom.bind(this);
		this.onEditStartType = this.onEditStartType.bind(this);
		this.onEditStartTime = this.onEditStartTime.bind(this);
		this.onEditMaxActivations = this.onEditMaxActivations.bind(this);

		this.onClickContentId = this.onClickContentId.bind(this);
		this.getStatusTextValidFrom = this.getStatusTextValidFrom.bind(this);
		this.getStatusTextValidThru = this.getStatusTextValidThru.bind(this);
		this.getStatusTextStyle = this.getStatusTextStyle.bind(this);
	}

	public componentDidMount() {
		this.getActivations();
	}

	private onClickContentId() {
		setTimeout(() => {
			if (this.state.contentCode !== undefined && this.state.contentCode.physioToolsId !== '') {
				const link = this.state.contentCode.physioToolsId !== ''
					? ('https://' + this.state.contentCode.physioToolsId + '.physiotoolsonline.com') : '#';
				window.open(link);
			}
		}, 1000);

		this.setState({
			clipboardMessage: new ResultMessage(Locale.get('contentCodeDetails_copiedToClipboard'), ResultStyle.Info)
		});
	}

	private getActivations() {
		if (this.state.contentCode === undefined) {
			return;
		}

		this.setState({
			loadInProgress: true
		});

		AppServices.contentService.getContentCodeActivations(this.state.contentCode.contentCode)
			.then((items: ContentCodeActivation[]) => {
				this.setState({
					activations: items,
					loadInProgress: false
				});
			}).catch(() => {
				this.setState({
					activations: undefined,
					loadInProgress: false
				});
			});
	}

	private setCodeState(active: boolean) {
		if (this.props.contentCode === undefined) return;

		let data = new ContentCodeEditData();
		data.contentCode = this.props.contentCode.contentCode;
		data.active = active;

		this.setState({
			active: active
		});

		this.onEdit(data);
	}

	private setActivationState(id: string, state: boolean) {
		if (this.props.contentCode === undefined) return;

		if (state === true) {
			AppServices.contentService.enableCodeActivation(this.props.contentCode.contentCode, id)
				.then((success: boolean) => {
					console.log("Result: " + success);
				});
		}
		else if (state === false) {
			AppServices.contentService.disableCodeActivation(this.props.contentCode.contentCode, id)
				.then((success: boolean) => {
					console.log("Result: " + success);
				});
		}
	}

	private onEditStartType(event: React.ChangeEvent<HTMLSelectElement>) {
		try {
			let value = event.target.value as keyof typeof ContentCodeStartType;
			this.setState({
				codeEdited: true,
				startType: ContentCodeStartType[value]
			});
		}
		catch (e) {
			console.log("Failed to parse ContentCodeStartType");
		}
	}

	private onEditStartTime(event: React.ChangeEvent<HTMLInputElement>) {
		this.setState({
			codeEdited: true,
			startTime: moment.utc(event.target.value),
			resultMessage: undefined
		});
	}

	private onEditValidFrom(event: React.ChangeEvent<HTMLInputElement>) {
		const value = moment.utc(event.target.value);
		this.setState({
			codeEdited: true,
			validFrom: value,
			startTime: value > this.state.startTime ? value : this.state.startTime,
			resultMessage: undefined
		});
	}

	private onEditValidThru(event: React.ChangeEvent<HTMLInputElement>) {
		let value = moment.utc(event.target.value);
		this.setState({
			codeEdited: true,
			validThru: value,
			startTime: value < this.state.startTime ? value : this.state.startTime,
			resultMessage: undefined
		});
	}

	private onEditMaxActivations(event: React.ChangeEvent<HTMLInputElement>) {
		let value = parseInt(event.target.value);

		if (value === undefined) {
			value = this.state.activations ? Math.max(this.state.activations.length, 1) : 1
		}

		this.setState({
			maxActivations: value,
			codeEdited: true,
			resultMessage: undefined
		});
	}

	private onSubmitChanges() {
		if (this.props.contentCode === undefined) return;

		let data = new ContentCodeEditData();
		data.contentCode = this.props.contentCode.contentCode;
		data.startType = this.state.startType;
		data.startTime = this.state.startTime.toISOString(true);
		data.validFrom = this.state.validFrom.startOf('day').toISOString(true);
		data.validThru = this.state.validThru.endOf('day').toISOString(true);
		data.maxActivations = this.state.maxActivations;

		this.onEdit(data);
	}

	private onEdit(data: ContentCodeEditData) {
		AppServices.contentService.editContentCode(data)
			.then((status: number) => {
				if (status === 204) // No content == success!
				{
					this.setState({
						codeEdited: false,
						resultMessage: new ResultMessage(
							Locale.get('contentCodeDetails_editSuccess'),
							ResultStyle.Success)
					});
				}
				else {
					console.log("Error: " + status);
					this.setState({
						resultMessage: new ResultMessage(
							Locale.get('contentCodeDetails_editFailed') + " (" + status + ")",
							ResultStyle.Error)
					});
				}

				UserProvider.refresh();
			});

	}

	private getStatusTextValidFrom(): string | undefined {
		if (this.state.validFrom > moment.utc()) {
			let diff = moment.duration(this.state.validFrom.diff(moment.utc()));
			let days = Math.ceil(diff.asDays()).toString();
			return Locale.format('contentCodeDetails_statusValidAfterDays', days);
		}
	}

	private getStatusTextValidThru(): string | undefined {
		if (this.state.validFrom > moment.utc()) {
			let diff = moment.duration(this.state.validThru.diff(this.state.validFrom));
			let days = Math.ceil(diff.asDays()).toString();
			return Locale.format('contentCodeDetails_statusWillBeVisibleForDays', days);
		}

		if (this.state.validThru > moment.utc()) {
			let diff = moment.duration(this.state.validThru.diff(moment.utc()));
			let days = Math.ceil(diff.asDays()).toString();
			return Locale.format('contentCodeDetails_statusVisibleForDays', days);
		}
		else {
			return Locale.get('contentCodeDetails_statusExpired');
		}
	}

	private getStatusTextStyle(): React.CSSProperties | undefined {
		if (this.state.validThru < moment.utc()) {
			return { color: 'red' };
		}
	}

	public render() {

		const formatAlias = (alias: string) => {
			const maxLen = 16;

			if (isValidEmailAddress(alias)) {
				return Locale.format('contentCodeDetails_activationUser_formatEmail', alias);
			}
			else if (alias.length > maxLen) {
				alias = alias.substring(0, maxLen) + '...';
			}

			return Locale.format('contentCodeDetails_activationUser_formatExt', alias);
		};

		if (this.state.contentCode === undefined) {
			return null;
		}
		else {
			return <div className='content-code-details'>

				<h1>{Locale.get('contentCodeDetails_title')}</h1>

				<div className='content-code-label'>
					<div className='content-code-info'>
						<h1>{Locale.get('contentCodeDetails_headerCode')}: {this.state.contentCode.contentCode}</h1>
						<form className='content-code-fields' onSubmit={this.onSubmitChanges} >
							<div className='content-code-field'>
								<b>{Locale.get('contentCodeDetails_headerContentId')}: </b>
								<Clipboard component='a' className='content-link'
									onClick={this.onClickContentId}
									data-clipboard-text={this.state.contentCode.contentId}>
									{this.state.contentCode.contentId}
								</Clipboard>
								<ResultMessageView message={this.state.clipboardMessage} />
							</div>
							<div className='content-code-field'>
								<b>{Locale.get('contentCodeDetails_headerStatus')}:</b>
								<Switch defaultChecked={this.state.contentCode.active} onValueChanged={this.setCodeState} />
								<p>
									{
										this.state.active
											? Locale.get('contentCodeDetails_statusActive')
											: Locale.get('contentCodeDetails_statusInactive')
									}
								</p>
							</div>
							<div className='content-code-field'>
								<b>{Locale.get('contentCodeDetails_headerStartType')}: </b>

								<select id='activation-type'
									defaultValue={ContentCodeStartType[this.state.startType]}
									onChange={this.onEditStartType}>
									<option value={ContentCodeStartType[ContentCodeStartType.Activation]}>
										{Locale.get("contentCodeType_Activation")}
									</option>
									<option value={ContentCodeStartType[ContentCodeStartType.Date]}>
										{Locale.get("contentCodeType_Date")}
									</option>
								</select>

								{
									this.state.startType === ContentCodeStartType.Date &&
									<input id='start-date' type='date' contentEditable={true}
										value={this.state.startTime.format('YYYY-MM-DD')}
										min={this.state.validFrom.format('YYYY-MM-DD')}
										max={this.state.validThru.format('YYYY-MM-DD')}
										onChange={this.onEditStartTime} />
								}
							</div>
							<div className='content-code-field'>
								<b>{Locale.get('contentCodeDetails_headerMaxActivations')}: </b>
								<UserConsumer>
									{context => <>
										{context.balance !== undefined && <>
											<input id='activations' type='number'
												defaultValue={this.state.maxActivations.toString()}
												min={this.state.activations ? Math.max(this.state.activations.length, 1) : 1}
												max={context.balance.available}
												onChange={this.onEditMaxActivations} />
											<p>/ {context.balance.available}</p>
										</>
										}
									</>
									}
								</UserConsumer>
							</div>
							<div className='content-code-field'>
								<b>{Locale.get('contentCodeDetails_headerValidFrom')}: </b>
								<input type='date' id='valid-from'
									defaultValue={this.state.validFrom.format('YYYY-MM-DD')}
									min={moment.utc().format('YYYY-MM-DD')}
									max={this.state.validThru.format('YYYY-MM-DD')}
									onChange={this.onEditValidFrom}
								/>
								<p>{this.getStatusTextValidFrom()}</p>
							</div>
							<div className='content-code-field'>
								<b>{Locale.get('contentCodeDetails_headerValidThru')}: </b>
								<input type='date' id='valid-thru'
									defaultValue={this.state.validThru.format('YYYY-MM-DD')}
									min={moment.max(this.state.validFrom, moment.utc()).format('YYYY-MM-DD')}
									onChange={this.onEditValidThru}
								/>
								<p style={this.getStatusTextStyle()}>
									{this.getStatusTextValidThru()}
								</p>
							</div>
							{this.state.codeEdited && <div className='btn' onClick={this.onSubmitChanges}>Tallenna</div>}
							<ResultMessageView message={this.state.resultMessage} />
						</form>
					</div>
				</div>

				<h2>{Locale.get("contentCodeDetails_headerActivations")}</h2>
				{this.state.loadInProgress && <Spinner />}
				{
					this.state.loadInProgress === false &&
					<div>
						{
							this.state.activations === undefined &&
							Locale.get("contentCodeDetails_activationLoadError")
						}
						{
							this.state.activations !== undefined &&
							this.state.activations.length === 0 &&
							Locale.get("contentCodeDetails_noActivations")
						}
					</div>
				}
				{
					this.state.loadInProgress === false &&
					this.state.activations !== undefined &&
					this.state.activations.length > 0 &&

					<table className='code-table'>
						<thead>
							<tr>
								<th>{Locale.get('contentCodeDetails_activationDate')}</th>
								<th>{Locale.get('contentCodeDetails_activationUser')}</th>
								<th>{Locale.get('contentCodeDetails_activationStatus')}</th>
							</tr>
						</thead>
						<tbody>
							{
								this.state.activations !== undefined &&
								this.state.activations.map((item, i) => {
									const date = moment.utc(item.startTime).format(Locale.get("format_dateTime"));

									return <tr key={i}>
										<td>{date}</td>
										<td>{formatAlias(item.userAlias)}</td>
										<td>
											<Switch defaultChecked={item.active}
												onValueChanged={
													(checked: boolean) => this.setActivationState(item.id, checked)
												}
											/>
										</td>
									</tr>
								})
							}
						</tbody>
					</table>
				}
			</div>;
		}
	}
}